import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { IconBuildingWarehouse, IconPlus, IconSearch } from '@tabler/icons';

import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const debounce = (func, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const ViewSuppliers = () => {
    const [appId, setAppId] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });

    const history = useHistory();

    useEffect(() => {
        (async () => {
            try {
                const response = await api.get('v1/apps/default/');
                if (response.data?.app?.id) {
                    setAppId(response.data.app.id);
                } else {
                    setStatus({ type: 'error', message: 'App padrão não encontrado' });
                }
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarFornecedores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const listarFornecedores = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/organizations/?is_supplier=true`);
            setSuppliers(response.data.organizations || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar fornecedores' });
        }
    };

    const handleSearch = useMemo(
        () =>
            debounce(async (value, currentAppId) => {
                if (!currentAppId) return;
                try {
                    const query = value ? `&search=${encodeURIComponent(value)}` : '';
                    const response = await api.get(`v1/apps/${currentAppId}/organizations/?is_supplier=true${query}`);
                    setSuppliers(response.data.organizations || []);
                } catch (error) {
                    console.error('Erro na busca:', error);
                }
            }, 400),
        []
    );

    const handleChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        handleSearch(value, appId);
    };

    const handleOpenSupplier = (supplierId) => {
        history.push(`/dashboard/config/organizations/edit/${supplierId}?type=supplier`);
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuildingWarehouse}
                gradient="coral"
                title="Fornecedores"
                subtitle="Empresas marcadas como fornecedor"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            size="small"
                            value={searchTerm}
                            onChange={handleChange}
                            placeholder="Buscar por nome, CNPJ..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/config/organizations/create?type=supplier')}
                        >
                            Adicionar fornecedor
                        </Button>
                    </Stack>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome Fantasia</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Telefone</TableCell>
                                <TableCell>Email</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {suppliers.map((supplier) => (
                                <TableRow key={supplier.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpenSupplier(supplier.id)}>
                                    <TableCell>
                                        <Typography fontWeight={600}>{supplier.name}</Typography>
                                    </TableCell>
                                    <TableCell>{supplier.cnpj || '—'}</TableCell>
                                    <TableCell>{supplier.phone || '—'}</TableCell>
                                    <TableCell>{supplier.email || '—'}</TableCell>
                                </TableRow>
                            ))}
                            {suppliers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Nenhum fornecedor cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default ViewSuppliers;
